import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import EmptyState from "../../shared/ui/EmptyState/EmptyState";
import PublicPresentationRenderer from "../../features/clientPresentation/widgets/PublicPresentationRenderer";
import {
  classifyPublicPresentationError,
  fetchPublicPresentation,
  postPublicPresentationEventBestEffort,
} from "../../features/clientPresentation/api/publicApi";
import { shouldRecordMeaningfulView } from "../../features/clientPresentation/lib/meaningfulView";
import { applyPublicPresentationDocumentMeta } from "../../features/clientPresentation/lib/documentMeta";
import { pickPublicProjection } from "../../features/clientPresentation/lib/publicProjection";
import {
  PublicPresentationThemeContext,
  readPublicPresentationTheme,
  togglePublicPresentationTheme,
  writePublicPresentationTheme,
} from "../../features/clientPresentation/lib/publicPresentationTheme";

function PublicPresentationScreen() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [presentation, setPresentation] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [theme, setTheme] = useState(readPublicPresentationTheme);
  const requestIdRef = useRef(0);
  const isDark = theme === "dark";

  useEffect(() => {
    const restore = applyPublicPresentationDocumentMeta({
      title: presentation?.title,
    });
    return () => {
      if (typeof restore === "function") restore();
    };
  }, [presentation]);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const controller = new AbortController();
    setStatus("loading");
    setPresentation(null);
    setMessage("");

    if (!token) {
      setStatus("not_found");
      setMessage("Цю презентацію не знайдено.");
      return undefined;
    }

    fetchPublicPresentation(token, { signal: controller.signal })
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setPresentation(pickPublicProjection(data));
        setStatus("ready");
      })
      .catch((error) => {
        if (error && error.code === "ERR_CANCELED") return;
        if (requestId !== requestIdRef.current) return;
        const classified = classifyPublicPresentationError(error);
        setPresentation(null);
        setStatus(classified.kind === "unavailable" ? "unavailable" : classified.kind === "not_found" ? "not_found" : "error");
        setMessage(classified.message);
      });

    return () => {
      controller.abort();
    };
  }, [token, retryNonce]);

  useEffect(() => {
    if (status !== "ready" || !token || !presentation) return undefined;
    if (!shouldRecordMeaningfulView(token)) return undefined;
    Promise.resolve(postPublicPresentationEventBestEffort(token, "view")).catch(
      () => undefined,
    );
    return undefined;
  }, [status, token, presentation]);

  const handlePhone = () => {
    Promise.resolve(postPublicPresentationEventBestEffort(token, "phone")).catch(
      () => undefined,
    );
  };
  const handleEmail = () => {
    Promise.resolve(postPublicPresentationEventBestEffort(token, "email")).catch(
      () => undefined,
    );
  };
  const handleThemeToggle = () => {
    const next = togglePublicPresentationTheme(theme);
    writePublicPresentationTheme(next);
    setTheme(next);
  };

  return (
    <PublicPresentationThemeContext.Provider value={theme}>
      <main
        className="presentation-page"
        id="presentation-page"
        data-testid="presentation-page"
        data-presentation-theme={theme}
      >
        <header className="presentation-page__brand">
          <div className="presentation-page__brand-mark">
            <img
              className="presentation-page__brand-logo"
              src={isDark ? "/images/logo-dark-nobg.png" : "/images/logo-nobg.png"}
              alt=""
            />
            <span>Рієстро</span>
          </div>
          <button
            type="button"
            className="presentation-page__theme"
            onClick={handleThemeToggle}
            aria-pressed={isDark}
            aria-label={isDark ? "Увімкнути світлу тему" : "Увімкнути темну тему"}
            data-testid="presentation-theme-toggle"
          >
            <i className={isDark ? "fa-solid fa-sun" : "fa-solid fa-moon"} aria-hidden="true" />
            {isDark ? "Світла" : "Темна"}
          </button>
        </header>
        {status === "loading" ? (
          <div className="presentation-skeleton" data-testid="presentation-skeleton" aria-busy="true">
            <div className="presentation-skeleton__hero" />
            <div className="presentation-skeleton__line" />
            <div className="presentation-skeleton__line presentation-skeleton__line--short" />
            <div className="presentation-skeleton__block" />
          </div>
        ) : null}
        {status === "not_found" ? (
          <EmptyState
            icon="fa-solid fa-compass"
            title="Презентацію не знайдено"
            description={message}
            size="lg"
          />
        ) : null}
        {status === "unavailable" ? (
          <EmptyState
            icon="fa-solid fa-link-slash"
            title="Презентація недоступна"
            description={message}
            size="lg"
          />
        ) : null}
        {status === "error" ? (
          <EmptyState
            icon="fa-solid fa-wifi"
            title="Не вдалося завантажити"
            description={message}
            size="lg"
            primaryAction={
              <button
                type="button"
                className="action-btn"
                onClick={() => setRetryNonce((current) => current + 1)}
              >
                Спробувати ще раз
              </button>
            }
          />
        ) : null}
        {status === "ready" && presentation ? (
          <PublicPresentationRenderer
            presentation={presentation}
            onPhoneActivate={handlePhone}
            onEmailActivate={handleEmail}
          />
        ) : null}
      </main>
    </PublicPresentationThemeContext.Provider>
  );
}

export default PublicPresentationScreen;
